import React from 'react'
import { Text } from '../ui'
import { BellIcon, EyeIcon } from '@heroicons/react/24/outline'
import { NotificationRecipientItem } from '../../services/notifications'
import DashboardSectionHeader from './DashboardSectionHeader'
import { DashboardCardSkeleton } from './DashboardSkeleton'
import { formatUserNameWithDepartment } from '../../utils/userDisplayName'

const DASHBOARD_NOTIFICATIONS_LIMIT = 5

interface NotificationsPanelProps {
  notifications: NotificationRecipientItem[]
  loading: boolean
  error: string | null
  sendersMap: Record<string, { name: string; avatarUrl: string | null; departmentName?: string | null }>
  onSelectNotification: (notification: NotificationRecipientItem) => void
  getInitials: (text: string) => string
  getAvatarColor: (text: string) => string
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  notifications,
  loading,
  error,
  sendersMap,
  onSelectNotification,
  getInitials,
  getAvatarColor,
}) => {
  const visibleNotifications = notifications.slice(0, DASHBOARD_NOTIFICATIONS_LIMIT)

  return (
    <section className="pt-0 pb-2 sm:pb-3 flex flex-col min-h-0">
      <DashboardSectionHeader
        compact
        icon={<BellIcon className="h-5 w-5" />}
        title="Últimas Notificações"
      />

      <div
        className="-mx-1 px-1 max-h-[20rem] overflow-y-auto overscroll-contain pr-1"
        aria-label="Lista das últimas 5 notificações"
      >
        {loading && (
          <div className="space-y-2">
            {Array.from({ length: DASHBOARD_NOTIFICATIONS_LIMIT }, (_, i) => (
              <DashboardCardSkeleton key={i} className="h-12" />
            ))}
          </div>
        )}

        {error && !loading && (
          <Text size="sm" className="text-red-600 dark:text-red-400">
            {error}
          </Text>
        )}

        {!loading && !error && notifications.length === 0 && (
          <div className="text-center py-5">
            <BellIcon className="h-9 w-9 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
            <Text size="sm" color="muted">
              Nenhuma notificação encontrada
            </Text>
          </div>
        )}

        {!loading && !error && visibleNotifications.length > 0 && (
          <ul className="pb-1">
            {visibleNotifications.map((notification, idx, arr) => {
              const senderId = notification.notification?.created_by
              const sender = senderId ? sendersMap[senderId] : null
              const senderName = sender?.name || 'Usuário'
              const senderLabel = formatUserNameWithDepartment(sender?.name, sender?.departmentName)
              const senderAvatarUrl = sender?.avatarUrl || null
              const isUnread = !notification.read_at

              return (
                <li key={notification.id} className="relative pb-3 last:pb-0">
                  {idx !== arr.length - 1 && (
                    <span
                      className="absolute top-3.5 left-3.5 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                      aria-hidden
                    />
                  )}
                  <div className="relative flex gap-2.5">
                    <span
                      className={`h-7 w-7 flex-shrink-0 rounded-full flex items-center justify-center ring-2 ring-white dark:ring-gray-800 overflow-hidden ${
                        senderAvatarUrl
                          ? ''
                          : isUnread
                            ? getAvatarColor(senderName)
                            : 'bg-gray-400 dark:bg-gray-600'
                      }`}
                    >
                      {senderAvatarUrl ? (
                        <img src={senderAvatarUrl} alt={senderName} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white font-semibold text-xs">{getInitials(senderName)}</span>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-1.5">
                        <Text
                          size="sm"
                          weight="bold"
                          className={`line-clamp-1 leading-snug ${
                            isUnread
                              ? 'text-gray-900 dark:text-white'
                              : 'text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {notification.notification?.title || 'Sem título'}
                        </Text>
                        {isUnread && (
                          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500 mt-1.5" />
                        )}
                      </div>
                      <Text size="xs" className="text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
                        {notification.notification?.body || 'Sem conteúdo'}
                      </Text>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <button
                          type="button"
                          onClick={() => onSelectNotification(notification)}
                          className="inline-flex items-center gap-0.5 text-[11px] font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 underline"
                        >
                          <EyeIcon className="h-3 w-3" />
                          Ver mensagem
                        </button>
                        <Text size="xs" className="text-gray-400 dark:text-gray-500 truncate">
                          {notification.notification?.created_at
                            ? new Date(notification.notification.created_at).toLocaleString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                              })
                            : '—'}
                          {senderLabel && ` · ${senderLabel}`}
                        </Text>
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}

export default NotificationsPanel
